import { Auth } from '@aws-amplify/auth';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Button, Card, Form } from 'react-bootstrap';
import VerificationInput from 'react-verification-input';

import { BrandContext } from '../context/BrandProvider';
import withHookedTheme from '../hocs/withHookedTheme';
import type { Theme } from '../theme/themes/ThemeTypes';
import ErrorLabel from '../ui/ErrorLabel';
import LoginLayout from '../ui/Layout/LoginLayout';
import LoginCard from '../ui/LoginCard';
import { debugLog } from '../utils/debugLog';
import {
  buildRedirectUrl as buildDomainRedirectUrl,
  getCurrentHostPrefix as getHostPrefix,
  hasRedirected,
} from '../utils/domainRedirect';
import ensureError from '../utils/ensureError';

type ChangeAuthState = (state: string, data?: unknown) => void;

type AuthData = { username?: string; user?: { username?: string } } | string | undefined;

interface LoopConfirmSignUpProps {
  theme: Theme;
  authState?: string;
  authData?: AuthData;
  changeState?: ChangeAuthState;
  onStateChange?: ChangeAuthState;
}

function usernameFromAuthData(authData: AuthData): string {
  if (!authData) return '';
  if (typeof authData === 'object') {
    return authData.user?.username || authData.username || '';
  }
  return authData;
}

/**
 * Confirm sign up screen. Talks to @aws-amplify/auth directly (no longer extends aws-amplify-react's ConfirmSignUp).
 */
const LoopConfirmSignUp: React.FC<LoopConfirmSignUpProps> = ({
  theme,
  authState,
  authData,
  changeState,
  onStateChange,
}) => {
  const brandCtx = useContext(BrandContext);
  const [resentEmail, setResentEmail] = useState(false);
  const [entryComplete, setEntryComplete] = useState(false);
  const [code, setCode] = useState('');
  const [usernameInput, setUsernameInput] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const validationInputRef = useRef<HTMLInputElement>(null);

  const goToState: ChangeAuthState = (state, data) => {
    if (typeof onStateChange === 'function') {
      onStateChange(state, data);
    } else if (typeof changeState === 'function') {
      changeState(state, data);
    }
  };

  const username = usernameFromAuthData(authData);

  // Focus the code input ourselves (with an unmount guard) rather than relying on the library's
  // autoFocus prop, so a discarded instance (e.g. under React Strict Mode) can't throw.
  useEffect(() => {
    if (!username) return undefined;
    const timer = setTimeout(() => validationInputRef.current?.focus(), 0);
    return () => clearTimeout(timer);
  }, [username]);

  /**
   * Reset the input field
   */
  const resetInputAndValidationInputComponent = useCallback((): void => {
    setCode('');
    setEntryComplete(false);
  }, []);

  // AuthController keeps this screen mounted while hidden, so reset local state each time the
  // flow is (re-)entered rather than retaining a previous attempt's code/error/resent state.
  useEffect(() => {
    if (authState !== 'confirmSignUp') return;
    setResentEmail(false);
    setUsernameInput('');
    setErrorMessage(undefined);
    resetInputAndValidationInputComponent();
  }, [authState, resetInputAndValidationInputComponent]);

  if (authState !== 'confirmSignUp') return null;

  const resend = async (): Promise<void> => {
    const resendUsername = username || usernameInput;
    try {
      await Auth.resendSignUp(resendUsername);
      // clear any error
      setErrorMessage(undefined);
      setResentEmail(true);
    } catch (err) {
      // A manually entered email means the raw failure (e.g. UserNotFoundException vs. an
      // already-confirmed account) could disclose whether that address is registered.
      debugLog('confirmSignUp', 'Unable to resend verification code', ensureError(err));
      setErrorMessage('Unable to resend the verification code. Please try again.');
      setResentEmail(false);
    }
  };

  const confirm = async (): Promise<void> => {
    const confirmUsername = username || usernameInput;

    debugLog(
      'confirmSignUp',
      JSON.stringify({
        t: new Date().toISOString(),
        event: 'LoopConfirmSignUp.confirm.attempt',
        username: confirmUsername,
        codeLength: code.length,
      })
    );

    if (!confirmUsername || !code) {
      setErrorMessage('Please enter your email and verification code.');
      return;
    }

    try {
      await Auth.confirmSignUp(confirmUsername, code);

      debugLog(
        'confirmSignUp',
        JSON.stringify({
          t: new Date().toISOString(),
          event: 'LoopConfirmSignUp.confirm.success',
          username: confirmUsername,
        })
      );

      const expectedPrefix = brandCtx?.domainPrefix;
      const currentPrefix = getHostPrefix();

      if (expectedPrefix && !brandCtx?.brandResolving) {
        // If we've already been redirected (query param present), do not redirect again
        if (!hasRedirected() && expectedPrefix !== currentPrefix) {
          window.location.href = buildDomainRedirectUrl(expectedPrefix, '/login', { redirected: 'confirmSignUp' });
          return;
        }
      }

      goToState('signIn');
    } catch (err) {
      const normalizedError = ensureError(err);
      debugLog(
        'confirmSignUp',
        JSON.stringify({
          t: new Date().toISOString(),
          event: 'LoopConfirmSignUp.confirm.error',
          username: confirmUsername,
          codeLength: code.length,
          errorName: (err as { name?: string })?.name,
          errorCode: (err as { code?: string })?.code,
          errorMessage: normalizedError.message,
        })
      );
      // A manually entered email means the raw Cognito failure could disclose whether that
      // address is registered - show a generic message instead of normalizedError.message.
      setErrorMessage('Unable to verify this account. Please check the code or request a new one.');
    }
  };

  const onKeyUp = (event: React.KeyboardEvent): void => {
    if (event.charCode === 13) {
      void confirm();
    }
  };

  return (
    <LoginLayout>
      <LoginCard>
        <Card.Title as="h1">Verify your email</Card.Title>
        {username && (
          <Card.Text>
            We sent a six digit verification code to:
            <span className="py-1 d-block"></span>
            <strong>{username}</strong>
            <span className="py-1 d-block"></span>
            Keep this window open while you check your inbox, then enter the code below.
            <span className="py-1 d-block"></span>
            If you don&apos;t see the email, you may need to check your spam folder, and ensure you&apos;ve typed in
            your email address correctly.
          </Card.Text>
        )}
        {!username && (
          <Card.Text>
            Please enter the email address that we sent your six digit verification code to, then enter the six digit
            code below.
            <span className="py-1 d-block"></span>
            If you don&apos;t see the email, you may need to check your spam folder, and ensure you&apos;ve typed in
            your email address correctly.
          </Card.Text>
        )}
        {errorMessage && <ErrorLabel label={errorMessage} />}
        <Form
          className="text-left mt-2"
          onSubmit={(e) => {
            e.preventDefault();
            void confirm();
            return false;
          }}
        >
          {!username && (
            <Form.Group>
              <Form.Label htmlFor="confirm_signup_username">Email address</Form.Label>
              <Form.Control
                id="confirm_signup_username"
                key="username"
                name="username"
                onChange={(e) => setUsernameInput(e.target.value.toLowerCase().trim())}
                type="email"
                placeholder="Email address"
                onKeyPress={onKeyUp}
              />
            </Form.Group>
          )}
          <Form.Group className="mb-2">
            <Form.Label htmlFor="confirm_signup_code">Six digit verification code</Form.Label>
            <VerificationInput
              ref={validationInputRef}
              value={code}
              validChars="0-9"
              placeholder=""
              onChange={(value) => {
                setCode(value);
                setEntryComplete(value.length === 6);
              }}
              inputProps={{
                id: 'confirm_signup_code',
                name: 'code',
              }}
              classNames={{
                container: 'verification-input-container',
                character: 'verification-input-character',
                characterInactive: 'verification-input-character--inactive',
                characterSelected: 'verification-input-character--selected',
              }}
            />
          </Form.Group>
          <Form.Group className="mb-0">
            {resentEmail ? (
              <p className="mb-0">Email resent</p>
            ) : (
              <p className="mb-0">
                Didn&apos;t get the code?{' '}
                <Button
                  variant="inline-link"
                  onClick={() => {
                    void resend();
                    resetInputAndValidationInputComponent();
                  }}
                >
                  Resend code
                </Button>
              </p>
            )}
          </Form.Group>
          <Form.Group>
            <Button type="submit" variant="primary" className="w-100" disabled={!entryComplete}>
              Submit
            </Button>
          </Form.Group>
        </Form>
        <div>
          <div className="or-panel">
            <hr />
            <span className="or-text">or</span>
            <hr />
          </div>

          <p className="mb-2">
            <Button
              variant="link"
              onClick={() => {
                goToState('signIn');
                resetInputAndValidationInputComponent();
              }}
            >
              Go back and try a different email address
            </Button>
          </p>
          <p className="mb-0">
            Still no email?{' '}
            <a href={theme.links.cantVerifyEmailSupport} target="_blank" rel="noreferrer">
              Go to {theme.language.appName} Support
            </a>
          </p>
        </div>
      </LoginCard>
    </LoginLayout>
  );
};

export default withHookedTheme(LoopConfirmSignUp);
